import { Injectable } from '@jack-henry/frontend-utils/di';
import { exists } from '@jack-henry/frontend-utils/functions';
import {
    DynamicComponentExitReason,
    DynamicComponentHandle,
    DynamicComponentRegistry,
    DynamicComponentResult,
} from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import { html, ReactiveElement, type TemplateResult } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';

import './side-drawer.component';
// eslint-disable-next-line no-duplicate-imports
import type { TmSideDrawer } from './side-drawer.component';
import {
    dispatchDrawerResult,
    DRAWER_CONTENT_CONTAINER_ID,
    SideDrawerOptions,
    SideDrawerProvider,
} from './side-drawer.service.types';

const DEFAULT_OPTIONS = Object.freeze<SideDrawerOptions>({
    host: window.document.body,
    align: 'right',
    buttons: [],
    footerAlignment: 'left',
});

type DrawerContent = string | TemplateResult;
/**
 * A service for managing a left- or right-side docked drawer populated with custom content.
 */
@Injectable()
export class TmSideDrawerService {
    constructor(private readonly registry: DynamicComponentRegistry<TmSideDrawer>) {}

    /**
     * Create a side drawer provider to be used within a drawer content component.
     *
     * Convenience function for use when implementing `SideDrawerProviderHost`.
     */
    public static createProvider<T>() {
        return new SideDrawerProvider<T>();
    }

    public get drawerOpen() {
        return exists(this.handle);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private handle?: DynamicComponentHandle<TmSideDrawer, any>;

    private readonly contentSource = new ObservationSource<Element>();

    private readonly sideDrawerRef = createRef<TmSideDrawer>();

    public open<T>(
        content: DrawerContent | ((setData: (data: T) => void) => DrawerContent),
        title: string,
        options: Partial<SideDrawerOptions> = {}
    ) {
        if (this.drawerOpen) {
            throw new Error(
                'Cannot open a side drawer; one already exists. Close it before opening again.'
            );
        }

        const allOptions = normalizeOptions(options);
        content =
            typeof content === 'function' ? content(data => this.dispatchData(data)) : content;
        const handle = this.registry.create<T>(() => {
            return this.renderDrawer(title, content, allOptions);
        }, allOptions);

        this.handle = handle;

        return handle.disposed;
    }

    public async close<T>(reason = DynamicComponentExitReason.ForceClose, data?: T) {
        if (!this.handle) {
            return;
        }

        const { handle } = this;
        this.handle = undefined;
        handle.dispose({
            reason,
            data,
        });
        await handle.disposed;
    }

    public async getDrawer() {
        if (!this.handle) {
            throw new Error('Could not get side drawer element. No drawer is open.');
        }

        const { element } = this.handle;
        await element.updateComplete;
        return element;
    }

    public getContent<E extends Element = Element>() {
        return this.contentSource.toPromise(true) as Promise<E>;
    }

    private async contentChanged(content?: Element) {
        if (!content) {
            return;
        }

        if (this.sideDrawerRef.value) {
            // firstElementChild won't be ready until after the update cycle
            await this.sideDrawerRef.value.updateComplete;
        }

        const { firstElementChild } = content;
        if (!firstElementChild) {
            throw new Error('Could not determine content element');
        }

        if (firstElementChild instanceof ReactiveElement) {
            await firstElementChild.updateComplete;
        }

        // content is the outer element;
        // the first child is the provided element
        this.contentSource.emit(firstElementChild);
    }

    /**
     * Dispatch from the active content element.
     * Intended to be passed to render hook.
     */
    private dispatchData<T>(data: T) {
        this.getContent().then(contentElement => dispatchDrawerResult(contentElement, data));
    }

    private renderDrawer<T>(
        title: string,
        content: string | TemplateResult,
        options: SideDrawerOptions
    ) {
        const { align, buttons, footerAlignment } = options;
        const alignment = align === 'left' ? 'left: 0' : 'right: 0';
        const style = `position: absolute; ${alignment}; top: 0; z-index: 1050;`;
        const contentTemplate = typeof content === 'string' ? html`<p>${content}</p>` : content;

        return html`<tm-side-drawer
            ${ref(this.sideDrawerRef)}
            style=${style}
            .alignment=${align}
            .heading=${title}
            .buttons=${buttons}
            .footerAlignment=${footerAlignment}
            @close-drawer=${(e: CustomEvent<DynamicComponentResult<T>>) => {
                // don't let the event bubble out
                e.preventDefault();
                const { reason, data } = e.detail || {};
                this.close(reason, data);
            }}
        >
            <div
                id="${DRAWER_CONTENT_CONTAINER_ID}"
                ${ref(content => this.contentChanged(content))}
            >
                ${contentTemplate}
            </div>
        </tm-side-drawer>`;
    }
}

function normalizeOptions(options: Partial<SideDrawerOptions>): SideDrawerOptions {
    return {
        ...DEFAULT_OPTIONS,
        ...options,
    };
}
